import { Autocomplete, TextField } from '@mui/material';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

import { API_BASE_URL } from '@synergy/config';
import { AccountApi } from '../../api/account-api';
import { SupplierApi } from '../../api/supplier-api';

/**
 * A filter for "which supplier", in the row above the table.
 *
 * The plain quick filter is a box or a dropdown, and neither answers this:
 * there are 3.434 suppliers and nobody types their id. The article list went
 * without the filter altogether although the list offers the field (#2239
 * R-139), so it gets the same kind of search the masks use — type a few
 * letters, pick a name, the list is narrowed by its id.
 */
export interface PaloSupplierFilterProps {
    /** The id currently filtered by, as it stands in the address. */
    value: string;
    onChange: (value: string) => void;
    /**
     * The name behind the id, as soon as it is known.
     *
     * The chip over the list would otherwise print the id — "Lieferant
     * 8.043", thousands separator and all — because nobody told it what the
     * key stands for (#2239 O-1, same as R-128).
     */
    onName?: (name: string) => void;
}

interface Option {
    id: number;
    name: string;
}

export const PaloSupplierFilter = ({ value, onChange, onName }: PaloSupplierFilterProps): ReactElement => {
    const api = useMemo(() => new SupplierApi(API_BASE_URL), []);
    const accounts = useMemo(() => new AccountApi(API_BASE_URL), []);
    const [search, setSearch] = useState('');
    const [options, setOptions] = useState<Option[]>([]);
    const [picked, setPicked] = useState<Option | null>(null);

    // The address may carry a supplier the user has not typed in this visit —
    // a kept filter, a shared link — so the name is looked up for the id.
    useEffect(() => {
        if (!value) {
            setPicked(null);

            return;
        }

        if (picked && String(picked.id) === value) {
            return;
        }

        let cancelled = false;

        // By its id, not by a search: the supplier list ignores an id it is
        // handed and would answer with the first of 3.434 — the wrong name
        // under the filter (measured 13.10.2026).
        void accounts.getContactInfo(Number(value)).then(response => {
            if (!cancelled && response.success) {
                setPicked({ id: response.result.id, name: response.result.name });
                onName?.(response.result.name);
            }
        });

        return () => {
            cancelled = true;
        };
        // The name is only looked up when the id changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useEffect(() => {
        if (search.length < 2) {
            return;
        }

        const controller = new AbortController();
        const timer = setTimeout(() => {
            void api
                .cget(new URLSearchParams({ search, limit: '20' }), controller.signal)
                .then(response =>
                    setOptions(
                        response.success ? response.result.items.map(item => ({ id: item.id, name: item.name })) : []
                    )
                );
        }, 300);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [api, search]);

    return (
        <Autocomplete<Option>
            size="small"
            value={picked}
            options={options}
            filterOptions={entries => entries}
            getOptionLabel={option => option.name}
            isOptionEqualToValue={(option, selected) => option.id === selected.id}
            noOptionsText="Lieferant suchen …"
            onInputChange={(_event, next) => setSearch(next)}
            onChange={(_event, next) => {
                setPicked(next);
                onChange(next ? String(next.id) : '');

                if (next) {
                    onName?.(next.name);
                }
            }}
            renderInput={params => <TextField {...params} placeholder="Alle" />}
            sx={{
                // Wider than the other quick filters: a supplier's name is a
                // name, and at 150 px it came out as "Schacher…" (#2239 O-1).
                width: 260,
                '& .MuiInputBase-root': { fontSize: '12px', backgroundColor: tokens.color.surface, padding: '0 6px' },
                '& .MuiInputBase-input': { padding: '4px 7px !important' },
            }}
        />
    );
};
